import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../shared/i18n';

/**
 * Reusable transfer panel — recipient picker, amount, memo + recent-memos.
 *
 * The transactions list is intentionally NOT here: it can grow unbounded, so
 * it lives on its own paginated page (TransactionsPage) reachable from the
 * row's "Transactions" button.
 */
export const CurrencyTransferPanel = ({ currency, api, busy, onTransferred }) => {
  const { t } = useTranslation();
  const [peers, setPeers] = useState([]);
  const [memos, setMemos] = useState([]);
  const [recipientAccountId, setRecipientAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    const [peersRes, memosRes] = await Promise.all([
      api.peers(currency.id),
      api.recentMemos(currency.id)
    ]);
    setPeers(peersRes.data?.peers || []);
    setMemos(memosRes.data?.memos || []);
  };

  useEffect(() => {
    reload().catch(() => {
      setError(t('family_currencies.errors.generic'));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency.id]);

  const peerLookup = useMemo(() => {
    const map = new Map();
    peers.forEach((p) => map.set(String(p.id), p));
    return map;
  }, [peers]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const parsedAmount = parseInt(amount, 10);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError(t('family_currencies.transfer.errors.invalid_amount'));
      return;
    }
    const peer = peerLookup.get(String(recipientAccountId));
    if (!peer) {
      setError(t('family_currencies.transfer.errors.invalid_recipient'));
      return;
    }
    setSubmitting(true);
    try {
      await api.transfer(currency.id, {
        recipient_account_id: peer.id,
        recipient_kind: peer.kind,
        recipient: peer.label,
        amount: parsedAmount,
        memo: memo.trim()
      });
      setAmount('');
      setMemo('');
      onTransferred?.();
      await reload();
    } catch (err) {
      const apiMessage = err.response?.data?.error?.message;
      setError(apiMessage || t('family_currencies.errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const disableForm = busy || submitting;

  return (
    <div className="border-top pt-3 mt-3" data-testid={`transfer-panel-${currency.id}`}>
      <h6 className="fw-bold mb-3">{t('family_currencies.transfer.title')}</h6>
      {error && (
        <div className="alert alert-danger" data-testid={`transfer-error-${currency.id}`}>{error}</div>
      )}

      <form onSubmit={submit} className="mb-3">
        <div className="mb-2">
          <label className="form-label small fw-semibold">
            {t('family_currencies.transfer.recipient_label')}
          </label>
          <select
            className="form-select"
            value={recipientAccountId}
            onChange={(e) => setRecipientAccountId(e.target.value)}
            disabled={disableForm}
            required
            data-testid={`transfer-recipient-${currency.id}`}
          >
            <option value="">
              {t('family_currencies.transfer.recipient_placeholder')}
            </option>
            {peers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.kind === 'child' ? '🧒' : '👤'} {p.label}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-2">
          <label className="form-label small fw-semibold">
            {t('family_currencies.transfer.amount_label')}
          </label>
          <input
            type="number"
            className="form-control"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="1"
            step="1"
            required
            disabled={disableForm}
            data-testid={`transfer-amount-${currency.id}`}
          />
        </div>
        <div className="mb-2">
          <label className="form-label small fw-semibold">
            {t('family_currencies.transfer.memo_label')}
          </label>
          <input
            type="text"
            className="form-control"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            maxLength={200}
            disabled={disableForm}
            data-testid={`transfer-memo-${currency.id}`}
          />
          {memos.length > 0 && (
            <div className="mt-1" data-testid={`recent-memos-${currency.id}`}>
              <span className="text-muted small me-2">
                {t('family_currencies.transfer.recent_memos')}:
              </span>
              {memos.map((m) => (
                <button
                  type="button"
                  key={m}
                  className="btn btn-sm btn-outline-secondary me-1 mb-1"
                  onClick={() => setMemo(m)}
                  disabled={disableForm}
                  data-testid={`recent-memo-${currency.id}`}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={disableForm}
          data-testid={`transfer-submit-${currency.id}`}
        >
          {t('family_currencies.transfer.submit')}
        </button>
      </form>
    </div>
  );
};

export default CurrencyTransferPanel;
